import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { adminStripeWebhookEvents } from "@/lib/admin-pos-payments.functions";
import { AlertTriangle, CheckCircle2, Clock, Ban } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/stripe-webhooks")({
  head: () => ({
    meta: [
      { title: "Admin — Stripe webhook events" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StripeWebhookEventsPage,
});

function fmt(ts: string | null) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("nl-NL", {
    dateStyle: "short",
    timeStyle: "medium",
  });
}
function money(cents: number | null) {
  if (cents == null) return "—";
  return `€ ${(cents / 100).toFixed(2)}`;
}

function outcomeBadge(outcome: string | null, processed_at: string | null) {
  if (!processed_at) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
        <Clock className="h-3 w-3" /> in behandeling
      </span>
    );
  }
  if (outcome === "ok") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
        <CheckCircle2 className="h-3 w-3" /> ok
      </span>
    );
  }
  if (outcome === "ignored") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
        <Ban className="h-3 w-3" /> genegeerd
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
      <AlertTriangle className="h-3 w-3" /> {outcome ?? "fout"}
    </span>
  );
}

function StripeWebhookEventsPage() {
  const fetchEvents = useServerFn(adminStripeWebhookEvents);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "stripe-webhook-events"],
    queryFn: () => fetchEvents(),
    refetchInterval: 15_000,
  });

  const stats = data?.stats;

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Stripe webhook events</h1>
          <p className="text-sm text-muted-foreground">
            Live overzicht van inkomende Stripe webhook-aanroepen (sandbox & live). Auto-refresh elke 15s.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
          disabled={isFetching}
        >
          {isFetching ? "Verversen…" : "Verversen"}
        </button>
      </div>

      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Laatste 24u" value={String(stats.total24h)} />
          <StatCard label="Geslaagd" value={String(stats.ok)} tone="ok" />
          <StatCard label="Genegeerd" value={String(stats.ignored)} tone="muted" />
          <StatCard label="Fouten" value={String(stats.errors)} tone="error" />
        </div>
      )}

      {isLoading && <p className="text-sm text-muted-foreground">Laden…</p>}
      {error && (
        <p className="text-sm text-red-600">
          Kon events niet laden: {(error as Error).message}
        </p>
      )}

      {data && (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Ontvangen</th>
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">Env</th>
                <th className="px-3 py-2">Event ID</th>
                <th className="px-3 py-2">PaymentIntent / Session</th>
                <th className="px-3 py-2 text-right">Bedrag</th>
                <th className="px-3 py-2">Order</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Fout / opmerking</th>
              </tr>
            </thead>
            <tbody>
              {data.events.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">
                    Nog geen webhook-events ontvangen.
                  </td>
                </tr>
              )}
              {data.events.map((ev: any) => (
                <tr key={ev.id} className="border-t align-top">
                  <td className="px-3 py-2 whitespace-nowrap">{fmt(ev.received_at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{ev.event_type ?? "—"}</td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        "rounded px-1.5 py-0.5 text-xs " +
                        (ev.environment === "live"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-blue-100 text-blue-800")
                      }
                    >
                      {ev.environment ?? "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs" title={ev.stripe_event_id ?? ""}>
                    {ev.stripe_event_id ? `${String(ev.stripe_event_id).slice(0, 16)}…` : "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {ev.stripe_payment_intent_id ?? ev.stripe_session_id ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right">{money(ev.amount_cents)}</td>
                  <td className="px-3 py-2 font-mono text-xs" title={ev.order_id ?? ""}>
                    {ev.order_id ? `${String(ev.order_id).slice(0, 8)}…` : "—"}
                  </td>
                  <td className="px-3 py-2">{outcomeBadge(ev.outcome, ev.processed_at)}</td>
                  <td className="max-w-md px-3 py-2 text-xs text-muted-foreground">
                    {ev.error_message ? (
                      <span className="text-red-700">{ev.error_message}</span>
                    ) : (
                      ev.note ?? "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok" | "error" | "muted";
}) {
  const toneClass =
    tone === "ok"
      ? "text-emerald-700"
      : tone === "error"
        ? "text-red-700"
        : tone === "muted"
          ? "text-slate-600"
          : "text-foreground";
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}
